import InfoIcon from "@mui/icons-material/Info";
import { Alert, Autocomplete, Box, Chip, CircularProgress, colors, IconButton, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import Grid from "@mui/material/Grid";
import { useTheme } from "@mui/material/styles";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, Cell as ChartCell, LabelList, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from "recharts";
import apiRequest from "../../../functions/api";

const round2 = (value) => Number(Number(value).toFixed(2));

function Saldenauszahlung(props) {
  const { t } = useTranslation();
  const {
    accessToken,
    client,
    element,
    program,
    ChangeFiles,
    lock,
    files,
    formular,
    formularkey,
    hper,
    felder,
    genehmigen,
    setSaldenauszahlungDisableButtons, //disabled Buttons in FormularEingabe.js
    ankeyRes,
    bearbeiten,
  } = props;

  //#region states
  const [summe, setSumme] = useState("");
  const [datumBeantragen, setDatumBeantragen] = useState("");
  const [standBeantragen, setstandBeantragen] = useState("");
  const [kontenData, setKontenData] = useState([]);
  const [selectedKonto, setSelectedKonto] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [helperText, setHelperText] = useState("");
  const [selectedKontoMitAntrag, setSelectedKontoMitAntrag] = useState(false);
  const [antragDatum, setAntragDatum] = useState("");

  //Eingabe der Summe (Auszahlung-Wert) muss disabled werden: Auszahlung bereits für Monat genehmigt und wir sind in /neu
  const [disableSummenEingabe, setDisableSummenEingabe] = useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));

  const aktuelleKontenDaten = kontenData?.find((k) => k.key === selectedKonto?.key);

  const WertChange = (field, value) => {
    setSumme(value);
    element.value = parseInt(value);
    element.antwort = parseInt(value);
    if (selectedKonto) {
      selectedKonto.saldenauszahlung = 1;
    }
    element.felderAntworten = {
      selectedKonto: selectedKonto,
      auszahlenSummeEingabe: value,
      error: error,
    };

    props.onChange(field, element);
  };

  useEffect(() => {
    if (selectedKonto && summe) {
      WertChange(element.key, summe);
    }
  }, [summe, error]);

  //Formular-Daten laden, wenn aus der Konten-Auswahl ein Konto ausgewählt wurde und bereits ein Formular für das jeweilige Konto gesendet wurde
  useEffect(() => {
    if (!genehmigen) {
      if (selectedKonto?.antragKz === "1" && selectedKonto?.antragKey) {
        const feldKey = element.key;

        const headers = {
          Authorization: "Bearer " + accessToken,
        };
        const payload = {
          mandant: client,
          program: "PMURA3",
          statement: "FORMULAR_LADEN",
          formularkey: selectedKonto.antragKey,
          formular: selectedKonto.antragKey?.substr(7, 3),
        };

        apiRequest("POST", "daten", payload, headers, (response) => {
          if (response.status === 200) {
            const antragFormular = "F" + selectedKonto.antragKey?.substr(7, 3);
            const antragFelder = response.data.felder?.[antragFormular];
            setAntragDatum(response?.data?.erstelldatum);
            setstandBeantragen(response?.data?.felder?.[formular]?.[feldKey]?.felderAntworten?.selectedKonto?.stand);
            setSelectedKonto(response?.data?.felder?.[formular]?.[feldKey]?.felderAntworten?.selectedKonto);
            if (response?.data?.status === "G" || response?.data?.status === "X") {
              //Formular ist für den aktuellen Zeitraum bereits genehmig oder exportiert -> Formular kann nicht abgesendet werden und die Eingabe eines Wertes ist nicht erlaubt
              setDisableSummenEingabe(true);
              setSaldenauszahlungDisableButtons(true);
            } else {
              setDisableSummenEingabe(false);
              setSaldenauszahlungDisableButtons(false);
            }
            if (antragFelder) {
              Object.values(antragFelder).forEach((feld) => {
                if (feld.art === "P" && feld.felderAntworten?.selectedKonto) {
                  //Wert aus Formular anpassen, Auszahlen-Feld übernimmt den Wert
                  validateInput(feld.felderAntworten.auszahlenSummeEingabe, selectedKonto);
                }
              });
            }
          }
        });
        setSelectedKontoMitAntrag(true);
      } else {
        setDisableSummenEingabe(false);
        setSelectedKontoMitAntrag(false);
      }
    }

    //Disable Buttons um Formular abzusenden, wenn der Stand negativ ist
    if (Number(selectedKonto?.stand) <= 0) {
      setSaldenauszahlungDisableButtons(true);
    }
    //Setze Error zurück
    setError("");
  }, [selectedKonto]);

  useEffect(() => {
    if (prevElementRef.current !== element.felderAntworten) {
      if (program !== "FORGEN") {
        const loadedKonto = element.felderAntworten?.selectedKonto;
        validateInput(element.felderAntworten?.auszahlenSummeEingabe, loadedKonto);
        if (loadedKonto) {
          setSelectedKonto(loadedKonto);
          setstandBeantragen(loadedKonto?.stand);
          setDatumBeantragen(loadedKonto?.stand_datum);
        }
        if (aktuelleKontenDaten && aktuelleKontenDaten?.antragKz === "0") {
          setstandBeantragen();
          setDatumBeantragen();
        }
      }
      prevElementRef.current = element.felderAntworten;
    }
  }, [element, program]);

  const validateInput = (value, konto = selectedKonto) => {
    const cleanValue = (value ?? "").toString().replace(/[^0-9]/g, "");
    setSumme(cleanValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = dateString.toString();
    const day = date.substring(6, 8);
    const month = date.substring(4, 6);
    const year = date.substring(0, 4);
    return `${day}.${month}.${year}`;
  };

  const getEinheit = (konto = selectedKonto) => (konto?.auszahlungsart === "T" ? "Tage" : "Std.");

  const prevElementRef = useRef(null);

  useEffect(() => {
    const headers = {
      Authorization: "Bearer " + accessToken,
    };
    const payload = {
      //Anpassen: VKEY der Person, für welche jeweiliger Antrag gestellt wurde
      anKey: ankeyRes ? ankeyRes : hper.KEY.inhalt,
      mandant: client,
      program: "FORMUL",
      statement: "SALDEN_INIT",
      formular: formular,
    };
    if (genehmigen) {
      payload.anKey = ankeyRes;
    }
    setIsLoading(true);
    setSaldenauszahlungDisableButtons(true);
    apiRequest("POST", "daten", payload, headers, (res) => {
      if ((res.status = 200) && res.data.konten) {
        console.log();
        setKontenData(res.data.konten);
        setIsLoading(false);
        setSaldenauszahlungDisableButtons(false);
      }
    });
  }, [ankeyRes, hper]);

  //Funktion setzt Error wenn Grenze oder Mindestwert unterschritten wird
  const validateValue = (value) => {
    const mindestAuszahlung = aktuelleKontenDaten?.mindestAuszahlung ?? 0;
    const grenze = aktuelleKontenDaten?.grenze ?? 0;
    const numericValue = parseInt(value) || 0;
    const einheit = getEinheit();
    if (numericValue < mindestAuszahlung) {
      setError(true);
      setHelperText(`Die Auszahlung muss mindestens ${mindestAuszahlung} ${einheit} betragen.`);
      //Disable FormularEingabe.js Buttons
      setSaldenauszahlungDisableButtons(true);
      return;
    } else {
      setError(false);
      setSaldenauszahlungDisableButtons(false);
      setHelperText("");
    }

    if (grenze) {
      const aktuellerStand = Number(aktuelleKontenDaten?.stand);

      const neuerStandCheck = round2(aktuellerStand - numericValue);

      if (neuerStandCheck < 0) {
        setError(true);
        setHelperText("Negative Werte sind nicht möglich.");
        //Disable FormularEingabe.js Buttons
        setSaldenauszahlungDisableButtons(true);
      } else if (neuerStandCheck < grenze) {
        setError(true);
        setHelperText(`Der neue Stand darf ${grenze} ${einheit} nicht unterschreiten.`);
        //Disable FormularEingabe.js Buttons
        setSaldenauszahlungDisableButtons(true);
        return;
      } else {
        setError(false);
        setSaldenauszahlungDisableButtons(false);

        setHelperText("");
      }
    }
  };

  const renderKontoCard = () => {
    if (!selectedKonto) return null;
    /* 
Es wird aus allen Werten (Mindeststand, standGenehmigung, standBeantragung, aktueller Stand ) der größte Wert gelesen und auf yAxisMax gespeichert.
Dieser Wert definiert die Höhe der Y-Achse
Die Reference Lines der Stände werden entweder zusammengefasst oder einzeiln geschrieben. Ein Schwellenwert definiert hier den Prozentsatz, ab wann zusammengefasst wird
*/

    const feldKey = element.key;
    const grenze = Number(aktuelleKontenDaten?.grenze ?? 0);
    const numericSumme = parseInt(summe) || 0;
    const aktuellerStand = aktuelleKontenDaten?.stand;
    const einheit = getEinheit();

    //standBeantragung ist der Stand aus Antwort Array. Dieser ist verfügbar, wenn das selectedKonto einen Antrag hat
    /* const standGenehmigung =
      selectedKonto?.antragKz === "1" && selectedKonto?.antragKey
        ? kontoDatenBeantragung?.felder?.[formular]?.[feldKey]?.felderAntworten?.selectedKonto?.standGenehmigung
        : undefined; */

    const standGenehmigung = selectedKonto?.standGenehmigung ? selectedKonto?.standGenehmigung : undefined;

    //numericSumme ist Eingabe des Wertes
    const neuerStandHeuteLive = round2(aktuellerStand - numericSumme);

    //Farben aus dem Theme. blocks.* sind bewusst blasse Hintergrundtöne (für Chips etc.) und im Lighttheme
    //kontrastarm auf weißem Grund - daher hier stattdessen die MUI-Standardpalette, die pro Mode automatisch
    //für ausreichenden Kontrast berechnet wird
    const farbeNeuerStand = theme.palette.primary.main;
    const farbeAktuellerStand = theme.palette.secondary.main;
    const farbeMindeststand = theme.palette.text.disabled;
    const farbeStandBeantragung = theme.palette.success.main;
    const farbeStandGenehmigung = theme.palette.warning.main;

    const chartData = [{ name: "Neuer Stand Konto " + selectedKonto?.bez, value: neuerStandHeuteLive, warn: neuerStandHeuteLive < grenze }];
    const anzeigeNeuerStand = neuerStandHeuteLive;

    //Y-Achse ist der Höchste Wert aus den Ständen
    const yAxisMax = Math.max(Number(standBeantragen) || 0, Number(aktuellerStand) || 0, grenze || 0, Number(standGenehmigung) || 0);

    const mindeststandUnterschritten = genehmigen && chartData[0]?.warn;

    //Referenzwerte, die nah beieinanderliegen, zu einer Linie mit gestapeltem Label zusammenfassen, damit sich Labels nicht überschneiden
    //referenzPunkte beeinhaltet alle Werte, die im Diagramm als referenceLines gerendert werden müssen
    const referenzPunkte = [
      grenze > 0 && { value: grenze, text: `Mindeststand: ${grenze} ${einheit}`, color: farbeMindeststand },
      { value: Number(aktuellerStand), text: `Aktueller Stand: ${aktuellerStand} ${einheit}`, color: farbeAktuellerStand },
      standGenehmigung !== undefined && {
        value: Number(standGenehmigung),
        text: `Stand bei Genehmigung: ${standGenehmigung} ${einheit}`,
        color: farbeStandGenehmigung,
      },
      { value: Number(standBeantragen), text: `Stand bei Beantragung: ${standBeantragen} ${einheit}`, color: farbeStandBeantragung },
    ].filter((eintrag) => eintrag && !isNaN(eintrag.value));

    //6% des Maximalwertes aus den Ständen ist der Schwellenwert
    const gruppierungsSchwelle = yAxisMax * 0.06;

    //referenzGruppen fasst die referenzPunkte zusammen, wenn Werte aus dem referenzPunkte Array zu nah beinander liegen
    const referenzGruppen = [...referenzPunkte]
      .sort((a, b) => a.value - b.value)
      .reduce((gruppen, punkt) => {
        const letzteGruppe = gruppen[gruppen.length - 1];
        if (letzteGruppe && punkt.value - letzteGruppe.wert < gruppierungsSchwelle) {
          letzteGruppe.mitglieder.push(punkt);
        } else {
          gruppen.push({ wert: punkt.value, mitglieder: [punkt] });
        }

        return gruppen;
      }, []);

    //Größte Gruppe bestimmt, wie viel zusätzlicher Platz oberhalb des Charts für gestapelte Labels nötig ist
    const groesstGruppenAnzahl = Math.max(1, ...referenzGruppen.map((gruppe) => gruppe.mitglieder.length));
    const chartMarginTop = isDesktop ? 24 + (groesstGruppenAnzahl - 1) * 14 : 24;

    const GruppenLabel = ({ viewBox, mitglieder }) => {
      if (!viewBox) return null;
      const zeilenHoehe = 14;
      return (
        <g>
          {mitglieder.map((mitglied, index) => (
            <text
              key={mitglied.text}
              x={viewBox.x + viewBox.width - 4}
              y={viewBox.y - 4 - (mitglieder.length - 1 - index) * zeilenHoehe}
              textAnchor="end"
              fontSize={isDesktop ? 12 : 10}
              fontWeight={600}
              fill={mitglied.color}
            >
              {mitglied.text}
            </text>
          ))}
        </g>
      );
    };

    //Kleine farbige Marker am Linienende auf Mobile, damit die Linie eindeutig dem passenden Legenden-Punkt zuzuordnen ist
    const GruppenMarker = ({ viewBox, mitglieder }) => {
      if (!viewBox) return null;
      const markerGroesse = 14;
      const abstand = markerGroesse + 4;
      return (
        <g>
          {mitglieder.map((mitglied, index) => (
            <rect
              key={mitglied.text}
              x={viewBox.x + viewBox.width - markerGroesse - index * abstand}
              y={viewBox.y - markerGroesse / 2}
              width={markerGroesse}
              height={markerGroesse}
              fill={mitglied.color}
            />
          ))}
        </g>
      );
    };

    const FarbPunkt = (farbe) => (
      <Box
        component="span"
        sx={{
          display: "inline-block",
          width: isDesktop ? 8 : 10,
          height: isDesktop ? 8 : 10,
          borderRadius: "50%",
          backgroundColor: farbe,
          mr: 0.75,
          flexShrink: 0,
          mt: isDesktop ? 0 : "4px",
        }}
      />
    );

    //Auf Mobile darf umgebrochen werden (kein "..."), auf Desktop bleibt alles einzeilig zentriert wie bisher
    const legendenZeilenStil = {
      justifyContent: isDesktop ? "center" : "flex-start",
      alignItems: isDesktop ? "center" : "flex-start",
      display: "flex",
      flexWrap: isDesktop ? "nowrap" : "wrap",
    };
    //Etwas größere Schrift auf Mobile für bessere Erkennbarkeit, Desktop unverändert
    const legendenTextGroesse = isDesktop ? undefined : 14;

    return (
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={0} justifyContent={"center"} alignContent={"center"}>
          <Grid item xs={1}></Grid>
          <Grid item xs={10}>
            <Typography fontWeight={"bold"} align="center">
              {selectedKonto?.bez}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <div style={legendenZeilenStil}>
              <Typography pr={1} color="text.primary" fontSize={legendenTextGroesse}>
                {"Beantragt" + ":"}
              </Typography>
              <Typography fontWeight={900} fontSize={legendenTextGroesse}>
                {summe} {einheit}
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div style={legendenZeilenStil}>
              {FarbPunkt(farbeAktuellerStand)}
              <Typography pr={1} fontSize={legendenTextGroesse}>
                {"Aktueller Stand" + (selectedKonto?.stand_datum ? ` (${formatDate(selectedKonto.stand_datum)})` : "") + ":"}
              </Typography>
              <Typography fontWeight={900} fontSize={legendenTextGroesse}>
                {aktuellerStand} {einheit}
              </Typography>
            </div>
          </Grid>

          <Grid item xs={12}>
            <div style={legendenZeilenStil}>
              {FarbPunkt(farbeNeuerStand)}
              <Typography pr={1} fontSize={legendenTextGroesse}>
                {(grenze > 0 ? `Neuer Stand (mind. ${grenze} ${einheit})` : "Neuer Stand") + ":"}
              </Typography>
              <Typography fontWeight={900} fontSize={legendenTextGroesse} sx={{ color: error ? theme.palette.error.main : theme.palette.primary.main }}>
                {anzeigeNeuerStand} {einheit}
              </Typography>
              {chartData[0]?.warn && (
                <Tooltip title={`Der Mindeststand von ${grenze} ${einheit} wird unterschritten.`}>
                  <InfoIcon sx={{ fontSize: 16, color: theme.palette.error.main, ml: 0.5, cursor: "pointer" }} />
                </Tooltip>
              )}
            </div>
          </Grid>

          {!isDesktop && grenze > 0 && (
            <Grid item xs={12}>
              <div style={legendenZeilenStil}>
                {FarbPunkt(farbeMindeststand)}
                <Typography pr={1} color="text.secondary" fontSize={legendenTextGroesse}>
                  {"Mindeststand" + ":"}
                </Typography>
                <Typography fontWeight={900} fontSize={legendenTextGroesse}>
                  {grenze} {einheit}
                </Typography>
              </div>
            </Grid>
          )}

          {standBeantragen && (
            <Grid item xs={12}>
              <div style={legendenZeilenStil}>
                {FarbPunkt(farbeStandBeantragung)}
                <Typography pr={1} color="text.secondary" fontSize={legendenTextGroesse}>
                  {"Stand bei Beantragung" + (datumBeantragen ? ` (${formatDate(datumBeantragen)})` : "") + ":"}
                </Typography>
                <Typography fontWeight={900} fontSize={legendenTextGroesse}>
                  {standBeantragen} {einheit}
                </Typography>
              </div>
            </Grid>
          )}

          {standGenehmigung && (
            <Grid item xs={12}>
              <div style={legendenZeilenStil}>
                {FarbPunkt(farbeStandGenehmigung)}
                <Typography pr={1} color="text.secondary" fontSize={legendenTextGroesse}>
                  {"Stand bei Genehmigung" + ":"}
                </Typography>
                <Typography fontWeight={900} fontSize={legendenTextGroesse}>
                  {standGenehmigung} {einheit}
                </Typography>
              </div>
            </Grid>
          )}

          <Grid item xs={12} sm={6} justifyContent={"center"} alignContent={"center"}>
            <ResponsiveContainer width="100%" height={isDesktop ? 280 : 260}>
              <BarChart data={chartData} margin={{ top: chartMarginTop, right: 16, left: 0, bottom: isDesktop ? 0 : 10 }}>
                <XAxis dataKey="name" fontSize={isDesktop ? 13 : 12} height={isDesktop ? 20 : 32} interval={0} />
                <YAxis fontSize={isDesktop ? 13 : 13} width={40} domain={[0, yAxisMax]} />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={80}>
                  <LabelList dataKey="value" position="top" formatter={(v) => `${v} ${einheit}`} fontSize={isDesktop ? 13 : 14} />
                  {chartData.map((entry, index) => (
                    <ChartCell key={index} fill={entry.warn ? theme.palette.error.main : farbeNeuerStand} />
                  ))}
                </Bar>
                {referenzGruppen.map((gruppe) => (
                  <ReferenceLine
                    key={gruppe.mitglieder.map((m) => m.text).join("-")}
                    y={gruppe.wert}
                    stroke={gruppe.mitglieder.length > 1 ? farbeMindeststand : gruppe.mitglieder[0].color}
                    strokeDasharray="4 4"
                    strokeWidth={isDesktop ? 1 : 2}
                    label={isDesktop ? <GruppenLabel mitglieder={gruppe.mitglieder} /> : <GruppenMarker mitglieder={gruppe.mitglieder} />}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </Grid>
          {mindeststandUnterschritten && (
            <Grid item xs={12} mb={1}>
              <Alert severity="warning">{`Achtung, der Mindeststand von ${grenze} ${einheit} wird unterschritten!`}</Alert>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  //#region JSX
  return (
    <Fragment>
      {isLoading ? (
        <Box p={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">
            Formular wird geladen …
          </Typography>
          <CircularProgress size={18} color="primary" />
        </Box>
      ) : (
        <>
          <Grid item xs={12} md={12} mb={2}>
            <Autocomplete
              value={selectedKonto || null}
              onChange={(event, value) => {
                setSelectedKonto(value);

                if (value && !value?.antragKey) {
                  WertChange(element.key, "");
                  setSaldenauszahlungDisableButtons(false);
                  setAntragDatum(false);
                }

                props.onChange(element.key, {
                  ...element,
                  felderAntworten: { ...element.felderAntworten, selectedKonto: value },
                });
              }}
              options={kontenData?.filter((konto) => konto.gesperrt !== "1") || []}
              getOptionLabel={(option) => option.bez}
              isOptionEqualToValue={(option, value) => option.key === value?.key}
              renderOption={(props, option) => {
                const antragStatusText = {
                  B: "beantragt",
                  G: "genehmigt",
                  X: "exportiert",
                }[option.antragStatus];

                return (
                  <li
                    {...props}
                    key={option.key}
                    style={{
                      ...props.style,
                      color: option.antragKz === "1" ? "gray" : "inherit",
                    }}
                  >
                    {option.bez}
                    {option.antragKz === "1" && ` – Für den aktuellen Lohnzeitraum ist bereits eine Auszahlung ${antragStatusText}`}
                  </li>
                );
              }}
              renderInput={(params) => <TextField {...params} label="Konto auswählen" fullWidth />}
              disabled={genehmigen || bearbeiten}
            />
          </Grid>
          {/* Chips anzeigen wenn Antrag auf Konto gestellt wurde */}
          {aktuelleKontenDaten?.antragKz === "1" && (
            <Grid item xs={12} md={12} mb={2}>
              <Box display="flex" gap={1}>
                <Chip size="small" label={"Antrag vom " + antragDatum} variant="outlined" style={{ backgroundColor: theme.palette.blocks.yellow }} />
                {aktuelleKontenDaten?.antragStatus === "G" && (
                  <Chip size="small" label="Genehmigt" variant="outlined" style={{ backgroundColor: theme.palette.blocks.green }} />
                )}
                {aktuelleKontenDaten?.antragStatus === "B" && (
                  <Chip size="small" label="Beantragt" variant="outlined" style={{ backgroundColor: theme.palette.blocks.yellow }} />
                )}
                {aktuelleKontenDaten?.antragStatus === "X" && (
                  <Chip size="small" label="Verarbeitet" variant="outlined" style={{ backgroundColor: theme.palette.blocks.green }} />
                )}
              </Box>
            </Grid>
          )}

          {/* Chip anzeigen wenn Stand des Kontos negativ oder 0 ist */}
          {Number(selectedKonto?.stand) <= 0 && (
            <Grid item xs={12} md={12} mb={2}>
              <Box display="flex" gap={1}>
                <Chip
                  size="small"
                  label="Konten mit Negativen Ständen nicht für Auszahlung zugelassen"
                  variant="outlined"
                  style={{ backgroundColor: theme.palette.blocks.yellow }}
                />
              </Box>
            </Grid>
          )}

          {selectedKonto && (
            <Grid item xs={12} md={12}>
              {renderKontoCard()}
            </Grid>
          )}

          <Grid item xs={12} md={12}>
            <TextField
              error={error}
              variant="outlined"
              value={summe}
              onChange={(event) => validateInput(event.target.value)}
              onBlur={(event) => validateValue(event.target.value)}
              fullWidth
              label={`Auszahlung (${getEinheit()})`}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                placeholder: "0",
                inputMode: "numeric",
              }}
              helperText={
                helperText || (aktuelleKontenDaten?.mindestAuszahlung ? `Mind. ${aktuelleKontenDaten?.mindestAuszahlung} ${getEinheit()}` : undefined)
              }
              disabled={lock || disableSummenEingabe}
            />
          </Grid>
        </>
      )}
    </Fragment>
  );
}

export default Saldenauszahlung;
